import { globalStyle, keyframes, style } from '@vanilla-extract/css';

/**
 * Agent 界面的视觉语言(D681):档案页名片、记忆块、Agent 列表页共用这一份。
 *
 * Agent 界面由统一的 Agent 视觉语言负责(不受应用设计系统扫描约束,见
 * `docs/architecture/design-system.md`),仍须与应用整体和谐:底色、文字、分隔线
 * 取应用 token,只有头像渐变、状态光点、强调色是这里自己的。全部变量以
 * `--agents-` 开头,集中在 `agentsTheme` 一处定义。取值按 Agent 视觉语言「灵动工作室」
 * (D682,`packages/extensions/agent-team/DESIGN.md`):状态色、标志色、身份色头像、
 * 暖色阴影与工作台同一套;底色与文字仍取应用 token,与应用整体和谐。
 */
export const agentsTheme = style({
  vars: {
    '--agents-radius-lg': '18px',
    '--agents-radius': '14px',
    '--agents-radius-sm': '10px',
    '--agents-surface': 'var(--xuwo-surface-muted, var(--xuwo-surface-canvas))',
    '--agents-surface-soft': 'var(--xuwo-surface-canvas-subtle)',
    '--agents-border': 'var(--xuwo-border-hairline)',
    '--agents-border-strong': 'var(--xuwo-border-control)',
    '--agents-text': 'var(--xuwo-text-primary)',
    '--agents-text-2': 'var(--xuwo-text-secondary)',
    '--agents-text-3': 'var(--xuwo-text-tertiary)',
    // 标志色(XuWo AI、牵头、焦点)与状态语言(DESIGN.md §2.2):
    // 在写 / 启用 = 绿,等你 = 琥珀,思考中 = 紫,排队 = 雾蓝,失败 = 珊瑚。
    '--agents-accent': 'oklch(0.55 0.21 285)',
    '--agents-accent-soft': 'oklch(0.96 0.03 285)',
    '--agents-live': 'oklch(0.58 0.14 160)',
    '--agents-live-soft': 'oklch(0.95 0.04 160)',
    '--agents-idle': 'oklch(0.62 0.02 70)',
    '--agents-idle-soft': 'oklch(0.95 0.008 80)',
    '--agents-warn': 'oklch(0.66 0.15 70)',
    '--agents-warn-soft': 'oklch(0.97 0.045 85)',
    '--agents-thinking': 'oklch(0.55 0.2 290)',
    '--agents-thinking-soft': 'oklch(0.95 0.035 290)',
    '--agents-queued': 'oklch(0.56 0.08 250)',
    '--agents-queued-soft': 'oklch(0.95 0.02 250)',
    '--agents-danger': 'oklch(0.58 0.17 25)',
    '--agents-danger-soft': 'oklch(0.96 0.03 25)',
    // 身份色头像:浅色是淡彩底配深字,深色是沉色底配浅字(§2.3)。
    '--agents-avatar-l': '0.9',
    '--agents-avatar-l2': '0.8',
    '--agents-avatar-c': '0.075',
    '--agents-avatar-ink': '#1D1B18',
    '--agents-glow':
      'linear-gradient(120deg, oklch(0.96 0.03 285), transparent 62%)',
    // 暖棕阴影:1px 描边 + 很淡的长投影;悬停抬起一档。
    '--agents-lift':
      '0 0 0 1px rgba(52,40,24,.07), 0 1px 2px rgba(52,40,24,.04), 0 12px 28px -20px rgba(60,40,20,.38)',
    '--agents-lift-hover':
      '0 0 0 1px rgba(52,40,24,.09), 0 2px 4px rgba(52,40,24,.05), 0 22px 40px -22px rgba(60,40,20,.45)',
  },
  color: 'var(--agents-text)',
  fontFamily: 'var(--xuwo-font-sans)',
  selectors: {
    '[data-theme="dark"] &': {
      vars: {
        '--agents-accent': 'oklch(0.74 0.16 285)',
        '--agents-accent-soft': 'oklch(0.27 0.06 285)',
        '--agents-live': 'oklch(0.76 0.13 160)',
        '--agents-live-soft': 'oklch(0.29 0.05 160)',
        '--agents-idle': 'oklch(0.66 0.02 70)',
        '--agents-idle-soft': 'oklch(0.27 0.008 70)',
        '--agents-warn': 'oklch(0.8 0.13 75)',
        '--agents-warn-soft': 'oklch(0.27 0.045 75)',
        '--agents-thinking': 'oklch(0.74 0.15 290)',
        '--agents-thinking-soft': 'oklch(0.3 0.07 290)',
        '--agents-queued': 'oklch(0.74 0.07 250)',
        '--agents-queued-soft': 'oklch(0.28 0.03 250)',
        '--agents-danger': 'oklch(0.72 0.14 25)',
        '--agents-danger-soft': 'oklch(0.28 0.05 25)',
        '--agents-avatar-l': '0.46',
        '--agents-avatar-l2': '0.36',
        '--agents-avatar-c': '0.085',
        '--agents-avatar-ink': '#F2EDE4',
        '--agents-glow':
          'linear-gradient(120deg, oklch(0.27 0.06 285), transparent 62%)',
        '--agents-lift':
          '0 0 0 1px rgba(255,240,220,.06), inset 0 1px 0 rgba(255,255,255,.035), 0 14px 30px -22px rgba(0,0,0,.9)',
        '--agents-lift-hover':
          '0 0 0 1px rgba(255,240,220,.1), inset 0 1px 0 rgba(255,255,255,.05), 0 24px 44px -24px rgba(0,0,0,.95)',
      },
    },
  },
});

/* ── 头像 ─────────────────────────────────────────────────────────────── */

export const avatar = style({
  position: 'relative',
  flex: 'none',
  display: 'grid',
  placeItems: 'center',
  // 圆角方块:半径 = 边长 × 0.32(§2.5)。
  borderRadius: '32%',
  color: 'var(--agents-avatar-ink)',
  fontWeight: 700,
  letterSpacing: '-0.02em',
  userSelect: 'none',
  // 色相由实体引用给(`--agents-hue`,10 个精选色相之一),改名不变色。
  background:
    'linear-gradient(145deg, oklch(var(--agents-avatar-l) var(--agents-avatar-c) var(--agents-hue)), oklch(var(--agents-avatar-l2) calc(var(--agents-avatar-c) * 1.45) calc(var(--agents-hue) + 34)))',
  boxShadow:
    'inset 0 -2px 0 rgba(0,0,0,.07), inset 0 1px 0 rgba(255,255,255,.35)',
  selectors: {
    '&[data-dimmed="true"]': { filter: 'grayscale(1)', opacity: 0.55 },
  },
});

/** 写者位上的头像外壳(带 Agent 主题,不撑开行高)。 */
export const writerAvatar = style({
  display: 'inline-flex',
  flex: 'none',
});

export const avatarEmoji = style({
  lineHeight: 1,
  filter: 'drop-shadow(0 1px 1px rgba(0, 0, 0, 0.18))',
});

export const avatarBadge = style({
  position: 'absolute',
  right: -2,
  bottom: -2,
  width: '30%',
  height: '30%',
  minWidth: 10,
  minHeight: 10,
  borderRadius: '50%',
  background: 'var(--agents-idle)',
  boxShadow: '0 0 0 2px var(--agents-surface)',
  selectors: {
    '&[data-live="true"]': { background: 'var(--agents-live)' },
  },
});

/* ── 状态胶囊 ─────────────────────────────────────────────────────────── */

const breathe = keyframes({
  '0%': { boxShadow: '0 0 0 0 var(--agents-live-soft)' },
  '70%': { boxShadow: '0 0 0 6px transparent' },
  '100%': { boxShadow: '0 0 0 0 transparent' },
});

export const pill = style({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 22,
  padding: '0 9px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 560,
  whiteSpace: 'nowrap',
  color: 'var(--agents-text-2)',
  background: 'var(--agents-idle-soft)',
  selectors: {
    '&[data-tone="live"]': {
      color: 'var(--agents-live)',
      background: 'var(--agents-live-soft)',
    },
    '&[data-tone="accent"]': {
      color: 'var(--agents-accent)',
      background: 'var(--agents-accent-soft)',
    },
    '&[data-tone="warn"]': {
      color: 'var(--agents-warn)',
      background: 'var(--agents-warn-soft)',
    },
  },
});

export const pillDot = style({
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: 'currentColor',
  selectors: {
    [`${pill}[data-tone="live"] &`]: {
      animation: `${breathe} 2.4s ease-out infinite`,
    },
  },
  '@media': {
    '(prefers-reduced-motion: reduce)': { animation: 'none' },
  },
});

/* ── 按钮 ─────────────────────────────────────────────────────────────── */

export const button = style({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  height: 30,
  padding: '0 12px',
  borderRadius: 999,
  border: '1px solid var(--agents-border-strong)',
  background: 'var(--agents-surface)',
  color: 'var(--agents-text)',
  fontSize: 13,
  fontWeight: 560,
  cursor: 'pointer',
  transition:
    'transform 160ms ease, box-shadow 160ms ease, background 160ms ease',
  selectors: {
    '&:hover': {
      background: 'var(--xuwo-state-hover)',
    },
    '&:active': { transform: 'scale(0.97)' },
    '&:focus-visible': {
      outline: '2px solid var(--agents-accent)',
      outlineOffset: 2,
    },
    '&:disabled': { opacity: 0.5, cursor: 'default', transform: 'none' },
    '&[data-variant="primary"]': {
      border: '1px solid transparent',
      color: '#fff',
      background:
        'linear-gradient(135deg, var(--agents-accent), color-mix(in srgb, var(--agents-accent) 70%, #a855f7))',
      boxShadow:
        '0 6px 16px -8px color-mix(in srgb, var(--agents-accent) 80%, transparent)',
    },
    '&[data-variant="primary"]:hover': {
      boxShadow:
        '0 10px 22px -10px color-mix(in srgb, var(--agents-accent) 90%, transparent)',
    },
    '&[data-variant="ghost"]': {
      border: '1px solid transparent',
      background: 'transparent',
      color: 'var(--agents-text-2)',
    },
  },
});

export const input = style({
  height: 30,
  width: 96,
  padding: '0 10px',
  borderRadius: 999,
  border: '1px solid var(--agents-border-strong)',
  background: 'var(--xuwo-surface-input, var(--agents-surface))',
  color: 'var(--agents-text)',
  fontSize: 13,
  fontVariantNumeric: 'tabular-nums',
  selectors: {
    '&:focus': {
      outline: 'none',
      borderColor: 'var(--agents-accent)',
      boxShadow: '0 0 0 3px var(--agents-accent-soft)',
    },
  },
});

export const muted = style({ color: 'var(--agents-text-3)' });

export const srOnly = style({
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
});

globalStyle(`${agentsTheme} *`, { boxSizing: 'border-box' });
